/**
 * SiteVisitsPanel — shared table used across Admin / Partner / Builder portals.
 * Admins can update the visit status; other roles view read-only.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Panel, Badge, EmptyState } from "../pages/portal/BuyerPortal";
import { Calendar, Clock, MapPin, Phone, User, Building2 } from "lucide-react";

const STATUS_TONE = {
    scheduled: "gold",
    confirmed: "green",
    completed: "green",
    cancelled: "red",
    no_show: "red",
};

const NEXT_STATUS = ["scheduled", "confirmed", "completed", "cancelled", "no_show"];

export default function SiteVisitsPanel({ endpoint, title = "Site Visits", canEdit = false, testid = "site-visits-panel", scopeLabel = null }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(endpoint);
            setItems(r.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateStatus = async (id, status) => {
        try { await api.put(`/admin/site-visits/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <Panel title={`${title} (${items.length})`} testid={testid}>
            {scopeLabel && <div className="text-[11px] text-black/50 mb-3">{scopeLabel}</div>}
            {loading ? (
                <div className="py-10 text-center text-black/40 text-sm">Loading site visits…</div>
            ) : items.length === 0 ? (
                <EmptyState msg="No site visits yet."/>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[820px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                                <th className="pb-3">Visitor</th>
                                <th className="pb-3">Property</th>
                                <th className="pb-3">Slot</th>
                                <th className="pb-3">Status</th>
                                {canEdit && <th className="pb-3">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((v) => (
                                <tr key={v.id} className="border-b border-black/8 last:border-b-0 align-top" data-testid={`visit-row-${v.id}`}>
                                    <td className="py-3">
                                        <div className="font-bold text-[13px] flex items-center gap-1.5"><User size={11}/> {v.visitor_name || v.user_name || "—"}</div>
                                        <div className="text-black/50 text-[11px] flex items-center gap-1.5 mt-0.5"><Phone size={10}/> {v.user_phone || "—"}</div>
                                        <div className="text-black/40 text-[11px]">{v.user_email || ""}</div>
                                    </td>
                                    <td className="py-3">
                                        <div className="font-bold text-[13px]">{v.property_title}</div>
                                        <div className="text-black/50 text-[11px] flex items-center gap-1.5 mt-0.5"><MapPin size={10}/> {v.property_city || "—"}</div>
                                        {v.property_developer && (
                                            <div className="text-black/40 text-[11px] flex items-center gap-1.5"><Building2 size={10}/> {v.property_developer}</div>
                                        )}
                                    </td>
                                    <td className="py-3">
                                        <div className="text-[12px] flex items-center gap-1.5 font-bold"><Calendar size={11} className="text-[var(--ebg-gold)]"/> {v.preferred_date || "—"}</div>
                                        <div className="text-black/60 text-[11px] flex items-center gap-1.5 mt-0.5"><Clock size={10}/> {v.preferred_time || v.slot || "TBD"}</div>
                                        {v.notes && <div className="text-black/45 text-[11px] mt-1 italic max-w-[220px]">&ldquo;{v.notes}&rdquo;</div>}
                                    </td>
                                    <td className="py-3"><Badge tone={STATUS_TONE[v.status] || "gold"}>{v.status || "scheduled"}</Badge></td>
                                    {canEdit && (
                                        <td className="py-3">
                                            <select
                                                value={v.status || "scheduled"}
                                                onChange={(e) => updateStatus(v.id, e.target.value)}
                                                data-testid={`visit-status-${v.id}`}
                                                className="text-[11px] border border-black/15 px-2 py-1.5 bg-white focus:border-black outline-none"
                                            >
                                                {NEXT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}
